import { type CaptchaState, useNosIdMutation } from '@/hooks/api/base.hooks';
import { api, type SecurityCode } from '@/portal-api.definition';
import { ActionType, useExecuteRecaptcha, useI18n, useToast } from '@nosinovacao/nosid-mfe-common';

type ContactType = 'Sms' | 'Email';

export function useSendCode() {
  const toast = useToast();
  const { t } = useI18n();
  const executeRecaptcha = useExecuteRecaptcha();

  const { mutate, mutateAsync, isPending, ...mutationProps } = useNosIdMutation({
    mutationFn: async ({
      captcha,
      targetId,
      type,
    }: {
      targetId: string;
      type: ContactType;
      resend?: boolean;
      captcha: CaptchaState;
    }) => {
      const token = captcha.challenge ? captcha.token : await executeRecaptcha('send_code');
      return api.post(
        '/api/v1/security/send-code',
        { TargetId: targetId, Type: type },
        { captcha: { token, challenge: captcha.challenge } },
      );
    },
    successLogAction: () => ({ ActionType: ActionType.Send }),
    errorLogAction: () => ({ ActionType: ActionType.Send }),
    onSuccess: (_, { resend }) => {
      toast.show('success', t(resend ? 'Common.SnackBar.ResendCode' : 'Common.SnackBar.CodeSent'));
    },
  });

  return { sendCode: mutate, sendCodeAsync: mutateAsync, isSending: isPending, ...mutationProps };
}

interface UseValidateCodeOptions {
  onSuccess?: (securityCode: SecurityCode) => void;
  onError?: () => void;
}

export function useValidateCode({ onSuccess, onError }: UseValidateCodeOptions) {
  const { mutate, isPending } = useNosIdMutation({
    mutationFn: ({
      code,
      contactType,
      targetId,
    }: {
      code: string;
      targetId: string;
      contactType: ContactType;
    }) =>
      api.post('/api/v1/security/validate-code', {
        TargetId: targetId,
        Type: contactType,
        Code: code,
      }),
    successLogAction: () => ({ ActionType: ActionType.Submit }),
    errorLogAction: () => ({ ActionType: ActionType.Submit }),
    onSuccess: (_, data) => {
      onSuccess?.({
        Code: data.code,
        TargetId: data.targetId,
        Type: data.contactType,
      } as SecurityCode);
    },
    onError,
  });

  return { validateCode: mutate, isValidating: isPending };
}
